import { useCallback, useState } from 'react';

import type { ConfirmArrivalQuantityField } from './types';

const cellKey = (lineId: string, field: ConfirmArrivalQuantityField) => `${lineId}:${field}`;

/**
 * Which quantity cells have their second input open — the same quantity in the
 * other unit, typed into directly. The legacy dialog showed both units in every
 * cell; here the second one appears on demand.
 *
 * Only visibility lives here: both inputs write the one inventory-unit value the
 * form holds, so opening or closing a cell never changes a quantity.
 */
export function useSecondaryUnitCells() {
  const [openCells, setOpenCells] = useState<ReadonlySet<string>>(() => new Set());

  const isSecondaryOpen = useCallback(
    (lineId: string, field: ConfirmArrivalQuantityField) => openCells.has(cellKey(lineId, field)),
    [openCells]
  );

  const toggleSecondary = useCallback(
    (lineId: string, field: ConfirmArrivalQuantityField) =>
      setOpenCells((prev) => {
        const next = new Set(prev);
        const key = cellKey(lineId, field);
        if (!next.delete(key)) next.add(key);
        return next;
      }),
    []
  );

  return { isSecondaryOpen, toggleSecondary, anySecondaryOpen: openCells.size > 0 };
}
